import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import { VhvVorvertragsdaten, VwgVorvertragsdaten } from '@ps20/bff-models';
import {
  App,
  areAppTypesEquivalent,
  Immobilie,
  isEqualForRelevantFields,
  Risikoort,
  VhvVorbelegungdaten,
  VN_ADRESSE_ID,
  VorvertragAdresse,
  VwgVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import get from 'lodash/get';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  transformRisikoortToImmobilie,
  transformRisikoortToImmobilieWithBaujahr,
} from '../reducers/immobilien.reducer';
import { IAppState } from '../appState';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { DashboardActions } from '../dashboard.actions';

export const ABS_ADRESSE_ID = 'ABS_ADRESSE_ID';

export function* watchImmobilieAusVorvertragRequested() {
  yield takeEvery(
    GlobalActions.VORBELEGUNG_FINISHED,
    safeSaga(successCallback, App.SHOP_DASHBOARD),
  );
}

export function* successCallback(successAction: GlobalAction<VorbelegungPayload>) {
  const appId = successAction.payload.appId;
  const angebotsId = successAction.payload.angebotsId;
  const vorbelegungFinishedForVHV = areAppTypesEquivalent(successAction.payload.appId, App.VHV);

  if (!areAppTypesEquivalent(successAction.payload.appId, App.VWG) && !vorbelegungFinishedForVHV) {
    return;
  }

  const vorbelegungsdaten: VhvVorbelegungdaten | VwgVorbelegungdaten = successAction.payload
    .vorhandeneVorbelegungdaten as VhvVorbelegungdaten | VwgVorbelegungdaten;

  if (vorbelegungsdaten == null) {
    return;
  }

  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const amisVNImmobilie = state.immobilien.find(immobilie => immobilie.id === VN_ADRESSE_ID);
  const absImmobilie = transformVorvertragAdresseToImmobilie(vorbelegungsdaten);

  if (
    !vorbelegungFinishedForVHV &&
    amisVNImmobilie &&
    !successAction.payload.beibehalten &&
    absImmobilie &&
    isEqualForRelevantFields(
      amisVNImmobilie.risikoort,
      vorvertragAdresseToRisikoort(vorbelegungsdaten.risikoort),
    )
  ) {
    handleImmobilienVorbelegung(appId, amisVNImmobilie, vorbelegungsdaten, state, angebotsId);
    yield call(
      globalPut.put,
      new GlobalActions().immobilieUebernehmen(amisVNImmobilie, appId, angebotsId),
    );
    yield call(
      globalPut.put,
      new GlobalActions().navigationRequested({
        appId: appId,
        subroute: '/eingabe',
        angebotsId,
      }),
    );
    yield call(closeImmobilienDialog);
  } else {
    handleImmobilienVorbelegung(appId, absImmobilie, vorbelegungsdaten, state, angebotsId);
    const immobilien: Array<Immobilie> = [];

    if (absImmobilie.risikoort.ort != null) {
      immobilien.push(absImmobilie);
    }

    if (
      amisVNImmobilie &&
      (!vorbelegungFinishedForVHV ||
        !isEqualForRelevantFields(amisVNImmobilie?.risikoort, absImmobilie?.risikoort))
    ) {
      immobilien.push(amisVNImmobilie);
    }

    if (absImmobilie) {
      absImmobilie.risikoort.status = get(vorbelegungsdaten.risikoort, 'status', null);
    }

    yield put(new DashboardActions().newImmobilienReceived(immobilien));
  }
}

function* closeImmobilienDialog() {
  yield put(new DashboardActions().immobilienDialogClose());
}

export function handleImmobilienVorbelegung(
  appId: App,
  immobilie: Immobilie,
  payload: VhvVorbelegungdaten | VwgVorbelegungdaten,
  state: IAppState,
  angebotsId: string,
) {
  if (areAppTypesEquivalent(appId, App.VHV)) {
    const vhvVorvertragsdaten: VhvVorvertragsdaten = payload as unknown as VhvVorvertragsdaten;

    if (vhvVorvertragsdaten.wohnflaeche) {
      immobilie.wohnflaeche = vhvVorvertragsdaten.wohnflaeche;
    }
    if (vhvVorvertragsdaten.hausart) {
      immobilie.hausart = vhvVorvertragsdaten.hausart;
    }
    if (vhvVorvertragsdaten.stockwerk) {
      immobilie.stockwerk = vhvVorvertragsdaten.stockwerk;
    }
    if (vhvVorvertragsdaten.hasElementar) {
      immobilie.hasElementar = vhvVorvertragsdaten.hasElementar;
    }
    immobilie.staendigBewohnt = vhvVorvertragsdaten.staendigBewohnt;
  } else if (areAppTypesEquivalent(appId, App.VWG)) {
    const vwgVorvertragsdaten: VwgVorvertragsdaten = payload as VwgVorvertragsdaten;

    if (vwgVorvertragsdaten.wohnflaeche) {
      immobilie.wohnflaeche = vwgVorvertragsdaten.wohnflaeche;
    }

    if (vwgVorvertragsdaten.baujahr != null) {
      immobilie.baujahr = vwgVorvertragsdaten.baujahr;
    }

    if (vwgVorvertragsdaten.nebengebaeude) {
      immobilie.nebengebaeudeVorhanden = vwgVorvertragsdaten.nebengebaeude;
    }

    if (shouldAddHasElementar(vwgVorvertragsdaten, state, angebotsId, appId, immobilie)) {
      immobilie.hasElementar = vwgVorvertragsdaten.hasElementar;
    }
  }
}

export function vorvertragAdresseToRisikoort(adresse: VorvertragAdresse): Risikoort {
  return {
    ort: get(adresse, 'ort', null),
    strasse: get(adresse, 'strasse', null),
    hausnrzus: get(adresse, 'hausnrzus', null),
    hausnummer: get(adresse, 'hausnummer', null),
    land: { text: '', wert: get(adresse, 'postlandSchluessel', null) },
    plz: get(adresse, 'plz', null),
    ortsteil: get(adresse, 'ortsteil', null),
    status: get(adresse, 'status', null),
  };
}

export const transformVorvertragAdresseToImmobilie = function (
  vorbelegungsdata: VwgVorbelegungdaten | VhvVorbelegungdaten,
): Immobilie {
  const risikoort: Risikoort = vorvertragAdresseToRisikoort(vorbelegungsdata.risikoort);
  if (areAppTypesEquivalent(vorbelegungsdata.forApp, App.VWG)) {
    return transformRisikoortToImmobilieWithBaujahr(
      risikoort,
      (vorbelegungsdata as VwgVorbelegungdaten).baujahr,
      ABS_ADRESSE_ID,
    );
  } else {
    return transformRisikoortToImmobilie(risikoort, ABS_ADRESSE_ID);
  }
};

function shouldAddHasElementar(
  vorvertragsdaten: VwgVorvertragsdaten,
  state: IAppState,
  angebotsId: string,
  appId: App,
  immobilie: Immobilie,
): boolean {
  const produkt: WarenkorbAngebot = state.warenkorb?.find(
    value => value.angebotsId === angebotsId && value.appId === appId,
  );
  if (!produkt || produkt.beibehalten) {
    return false;
  }
  return isEqualForRelevantFields(immobilie?.risikoort, produkt?.risikoortForElementar);
}
